import { useCallback, useEffect } from 'react';
import { Divider, Paper } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { STORE_DELIVERY_ADDRESS } from '../../constants/store-delivery-address.constants';
import { toFormDeliveryType } from '../../constants/delivery-type.constants';
import { DeliveryTypeSelector } from '../Forms/DeliveryTypeSelector.component';
import { HomeDeliverySection } from '../Forms/HomeDeliverySection.component';
import { RelayDeliverySection } from '../Forms/RelayDeliverySection.component';
import { StoreDeliverySection } from '../Forms/StoreDeliverySection.component';
import { OrganizationDeliverySection } from '../Forms/OrganizationDeliverySection.component';
import { DeliverySummaryDisplay } from '../Forms/DeliverySummaryDisplay.component';
import { AddressDisplayForm } from '../Forms/AddressDisplayForm.component';
import { DeliveryAddress, DeliveryDay, DeliveryType, UserAddress } from '../../types/delivery-selection.type';
import { Order } from '../../types/order.type';

type Props = {
  addresses: UserAddress[];
  lastOrder: Order | null;
  defaultOrganizationId?: string;
  selectedDeliveryType: DeliveryType;
  selectedDeliveryAddress: DeliveryAddress | null;
  selectedBillingAddress: DeliveryAddress | null;
  selectedDeliveryDay: DeliveryDay | null;
  selectedDeliveryPointId: string | null;
  onDeliveryTypeChange: (type: DeliveryType) => void;
  onDeliveryAddressChange: (address: DeliveryAddress | null) => void;
  onBillingAddressChange: (address: DeliveryAddress | null) => void;
  onDeliveryDayChange: (day: DeliveryDay | null) => void;
  onDeliveryPointChange: (deliveryPointId: string | null) => void;
  onOrganizationChange: (organizationId: string | null) => void;
};

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',

    '& > h4': {
      margin: 0,
      opacity: 0.7,
    },
  },

  paper: {
    padding: theme.spacing(2),
    overflow: 'auto',

    '& > section:first-of-type': {
      display: 'grid',
      gridTemplateColumns: '1.5fr 3fr 3fr',
      gap: '1rem',
      marginBottom: '1rem',
    },
  },

  deliveryAddress: {
    display: 'flex',
    alignItems: 'start',
    justifyContent: 'space-between',
    gap: '1rem',
    marginBottom: '1rem',

    '& > div': {
      display: 'flex',
      flexDirection: 'column',
      width: 350,
      gap: '1rem',

      '& > h4': {
        margin: 0,
      },
    },
  },
}));

export function DeliveryInfoSection({
  addresses,
  lastOrder,
  defaultOrganizationId,
  selectedDeliveryType,
  selectedDeliveryAddress,
  selectedBillingAddress,
  selectedDeliveryDay,
  selectedDeliveryPointId,
  onDeliveryTypeChange,
  onDeliveryAddressChange,
  onBillingAddressChange,
  onDeliveryDayChange,
  onDeliveryPointChange,
  onOrganizationChange,
}: Props) {
  const classes = useStyles();

  const handleDeliveryTypeChange = useCallback(
    (type: DeliveryType) => {
      onDeliveryTypeChange(type);
      onDeliveryAddressChange(type === 'bioculture' ? STORE_DELIVERY_ADDRESS : null);
      onBillingAddressChange(null);
      onDeliveryDayChange(null);
      onDeliveryPointChange(null);
      onOrganizationChange(null);
    },
    [
      onDeliveryTypeChange,
      onDeliveryAddressChange,
      onBillingAddressChange,
      onDeliveryDayChange,
      onDeliveryPointChange,
      onOrganizationChange,
    ]
  );

  const lastOrderDeliveryType = lastOrder?.deliveryInfos?.deliveryType
    ? toFormDeliveryType(lastOrder.deliveryInfos.deliveryType)
    : null;

  useEffect(() => {
    if (lastOrderDeliveryType) {
      handleDeliveryTypeChange(lastOrderDeliveryType);
    }
  }, [lastOrder, lastOrderDeliveryType, handleDeliveryTypeChange]);

  const showSummary = selectedDeliveryType && selectedDeliveryAddress && selectedDeliveryDay;

  return (
    <section className={classes.root}>
      <h4>Informations de livraison</h4>
      <Paper className={classes.paper}>
        <section>
          <DeliveryTypeSelector value={selectedDeliveryType} onChange={handleDeliveryTypeChange} />

          {selectedDeliveryType === 'home' && (
            <HomeDeliverySection
              addresses={addresses}
              selectedAddress={selectedDeliveryAddress}
              selectedDay={selectedDeliveryDay}
              onAddressChange={onDeliveryAddressChange}
              onBillingAddressChange={onBillingAddressChange}
              onDayChange={onDeliveryDayChange}
              lastOrder={lastOrderDeliveryType === 'home' ? lastOrder : undefined}
            />
          )}
          {selectedDeliveryType === 'relayPoint' && (
            <RelayDeliverySection
              selectedAddress={selectedDeliveryAddress}
              selectedDay={selectedDeliveryDay}
              onAddressChange={onDeliveryAddressChange}
              onBillingAddressChange={onBillingAddressChange}
              onDayChange={onDeliveryDayChange}
              lastOrder={lastOrderDeliveryType === 'relayPoint' ? lastOrder : undefined}
            />
          )}
          {selectedDeliveryType === 'bioculture' && (
            <StoreDeliverySection selectedDay={selectedDeliveryDay} onDayChange={onDeliveryDayChange} />
          )}
          {selectedDeliveryType === 'organization' && (
            <OrganizationDeliverySection
              selectedAddress={selectedDeliveryAddress}
              selectedDay={selectedDeliveryDay}
              selectedDeliveryPointId={selectedDeliveryPointId}
              defaultOrganizationId={defaultOrganizationId}
              onAddressChange={onDeliveryAddressChange}
              onBillingAddressChange={onBillingAddressChange}
              onDayChange={onDeliveryDayChange}
              onDeliveryPointChange={onDeliveryPointChange}
              onOrganizationChange={onOrganizationChange}
            />
          )}
        </section>

        {showSummary && (
          <>
            <Divider style={{ marginBottom: '1rem' }} light />
            <DeliverySummaryDisplay deliveryType={selectedDeliveryType} deliveryDay={selectedDeliveryDay} />

            <section className={classes.deliveryAddress}>
              <div>
                <AddressDisplayForm
                  address={selectedDeliveryAddress}
                  onChange={onDeliveryAddressChange}
                  editable={selectedDeliveryType !== 'bioculture' && selectedDeliveryType !== 'organization'}
                  label="Adresse de livraison :"
                />
              </div>
              <div>
                <AddressDisplayForm
                  address={selectedBillingAddress}
                  onChange={onBillingAddressChange}
                  label="Adresse de facturation :"
                />
              </div>
            </section>
          </>
        )}
      </Paper>
    </section>
  );
}
